import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import AccessDenied from './AccessDenied';

/**
 * HOC para proteger rotas que requerem permissão específica
 * 
 * @param {React.Component} Component - Componente a ser protegido
 * @param {string} permissionName - Nome da permissão necessária (ex: 'tipo_produto.list')
 * @param {number} minLevel - Nível de acesso mínimo (opcional)
 * @param {string} message - Mensagem personalizada de acesso negado (opcional)
 * 
 * @example
 * export default withPermission(TipoProdutoList, 'tipo_produto.list', 3);
 */
const withPermission = (Component, permissionName, minLevel = null, message = null) => {
  return function ProtectedComponent(props) {
    const { user, hasPermission } = useAuth();

    // Verificar se está autenticado
    if (!user) {
      return <AccessDenied message="Você precisa estar autenticado para acessar esta página." />;
    }

    // Verificar nível de acesso se especificado
    if (minLevel !== null && user.nivel_acesso < minLevel) {
      return <AccessDenied message={message || 'Você não tem nível de acesso suficiente para acessar esta página.'} />;
    }

    // Verificar permissão
    if (!hasPermission(permissionName)) {
      return <AccessDenied message={message || 'Você não tem permissão para acessar esta página.'} />;
    }

    // Usuário tem permissão, renderizar componente
    return <Component {...props} />;
  };
};

/**
 * HOC para proteger rotas que requerem permissão E nível de acesso
 * 
 * @param {React.Component} Component - Componente a ser protegido
 * @param {string} permissionName - Nome da permissão necessária
 * @param {number} minLevel - Nível de acesso mínimo necessário
 * @param {string} message - Mensagem personalizada (opcional)
 * 
 * @example
 * export default withPermissionAndLevel(TipoProdutoEdit, 'tipo_produto.update', 3);
 */
const withPermissionAndLevel = (Component, permissionName, minLevel, message = null) => {
  return withPermission(Component, permissionName, minLevel, message);
};

/**
 * Hook personalizado para verificar permissões dentro de componentes
 * 
 * @example
 * const { checkPermission, checkPermissionAndLevel } = usePermissionCheck();
 * if (checkPermission('tipo_produto.create')) {
 *   // Mostrar botão criar
 * }
 */
export const usePermissionCheck = () => {
  const { user, hasPermission } = useAuth();

  const checkPermission = (permissionName) => {
    return hasPermission(permissionName);
  };

  const checkPermissionAndLevel = (permissionName, minLevel) => {
    if (!user || user.nivel_acesso < minLevel) {
      return false;
    }
    return hasPermission(permissionName);
  };

  const checkLevel = (minLevel) => {
    return user && user.nivel_acesso >= minLevel;
  };

  return {
    checkPermission,
    checkPermissionAndLevel,
    checkLevel,
    userLevel: user?.nivel_acesso || 0
  };
};

export default withPermission;
export { withPermissionAndLevel };
